package com.fantasytenis.web.screens.survivor

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.survivor.entity.*
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen for selecting a player pick in a survivor contest.
 */
@Composable
fun SurvivorPickSelectionScreen(contestId: String, entryId: String) {
    val scope = rememberCoroutineScope()
    var contest by remember { mutableStateOf<SurvivorContestEntity?>(null) }
    var entryWithPicks by remember { mutableStateOf<SurvivorEntryWithPicksEntity?>(null) }
    var availablePlayers by remember { mutableStateOf<List<AvailablePlayerEntity>>(emptyList()) }
    var selectedPlayerId by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var isSubmitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var sortBy by remember { mutableStateOf(SortOption.OWNERSHIP) }
    var filterText by remember { mutableStateOf("") }
    var showUsedPlayers by remember { mutableStateOf(false) }

    // Load data
    LaunchedEffect(entryId) {
        isLoading = true
        error = null

        // Load contest
        DependencyContainer.getSurvivorContestUseCase(contestId).fold(
            onSuccess = { contest = it },
            onFailure = { error = it.message }
        )

        // Load entry with picks
        DependencyContainer.getSurvivorEntryUseCase(entryId).fold(
            onSuccess = { entryWithPicks = it },
            onFailure = { error = it.message }
        )

        // Load available players
        DependencyContainer.getAvailablePlayersUseCase(entryId).fold(
            onSuccess = {
                availablePlayers = it
                isLoading = false
            },
            onFailure = {
                error = it.message
                isLoading = false
            }
        )
    }

    // Check if pick already made for current round
    val currentRoundPick = remember(entryWithPicks, contest) {
        entryWithPicks?.let { entry ->
            contest?.currentRound?.let { round ->
                entry.getPickForRound(round)
            }
        }
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        Header({
            style {
                flexShrink(0)
                backgroundColor(Colors.Primary)
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                Button(
                    text = "Back",
                    onClick = { NavigationState.navigate(Route.SurvivorContestDetail(contestId)) },
                    variant = "secondary"
                )
                H2({
                    style {
                        color(Colors.White)
                        fontSize(20.px)
                        fontWeight(700)
                        margin(0.px)
                    }
                }) {
                    Text("Make Your Pick")
                }
            }

            // Current round indicator
            contest?.currentRound?.let { round ->
                Div({
                    style {
                        backgroundColor(rgba(255, 255, 255, 0.2))
                        padding(8.px, 16.px)
                        borderRadius(20.px)
                        color(Colors.White)
                        fontSize(14.px)
                        fontWeight(500)
                    }
                }) {
                    Text(round.displayName)
                }
            }
        }

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        error = null
                        DependencyContainer.getAvailablePlayersUseCase(entryId).fold(
                            onSuccess = { availablePlayers = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                else -> {
                    // Success message
                    successMessage?.let { msg ->
                        SuccessMessage(msg) { successMessage = null }
                    }

                    // Already picked warning
                    currentRoundPick?.let { pick ->
                        CurrentPickCard(pick, contest!!)
                    }

                    // Entry status card
                    entryWithPicks?.let { data ->
                        contest?.let { c ->
                            EntryStatusCard(data.entry, c)
                        }
                    }

                    // Filters and sort
                    FilterSortBar(
                        filterText = filterText,
                        onFilterChange = { filterText = it },
                        sortBy = sortBy,
                        onSortChange = { sortBy = it },
                        showUsedPlayers = showUsedPlayers,
                        onShowUsedChange = { showUsedPlayers = it }
                    )

                    // Available players grid
                    val filteredPlayers = availablePlayers
                        .filter { player ->
                            (showUsedPlayers || !player.alreadyUsed) &&
                            (filterText.isEmpty() ||
                             player.playerName.contains(filterText, ignoreCase = true) ||
                             player.opponentName?.contains(filterText, ignoreCase = true) == true)
                        }
                        .sortedWith(
                            when (sortBy) {
                                SortOption.OWNERSHIP -> compareByDescending { it.ownershipPercentage }
                                SortOption.SEED -> compareBy { it.seed ?: 999 }
                                SortOption.RANKING -> compareBy { it.atpRanking ?: 999 }
                                SortOption.MATCH_TIME -> compareBy { it.matchTime ?: "" }
                            }
                        )

                    if (filteredPlayers.isEmpty()) {
                        EmptyState("No players match your filters")
                    } else {
                        PlayersGrid(
                            players = filteredPlayers,
                            selectedPlayerId = selectedPlayerId,
                            onSelectPlayer = { player ->
                                if (player.isAvailable) {
                                    selectedPlayerId = if (selectedPlayerId == player.playerId) null else player.playerId
                                }
                            },
                            currentPickId = currentRoundPick?.playerId
                        )
                    }

                    // Submit button
                    if (selectedPlayerId != null && selectedPlayerId != currentRoundPick?.playerId) {
                        SubmitPickBar(
                            selectedPlayer = availablePlayers.find { it.playerId == selectedPlayerId },
                            isSubmitting = isSubmitting,
                            isUpdate = currentRoundPick != null,
                            onSubmit = {
                                scope.launch {
                                    isSubmitting = true
                                    error = null

                                    val result = if (currentRoundPick != null) {
                                        // Update existing pick
                                        DependencyContainer.updateSurvivorPickUseCase(
                                            entryId = entryId,
                                            pickId = currentRoundPick.id,
                                            playerId = selectedPlayerId!!
                                        )
                                    } else {
                                        // Make new pick
                                        DependencyContainer.makeSurvivorPickUseCase(
                                            entryId = entryId,
                                            playerId = selectedPlayerId!!
                                        )
                                    }

                                    result.fold(
                                        onSuccess = { pick ->
                                            successMessage = "Pick confirmed: ${pick.playerName}"
                                            // Reload data
                                            DependencyContainer.getSurvivorEntryUseCase(entryId).fold(
                                                onSuccess = { entryWithPicks = it },
                                                onFailure = { }
                                            )
                                            DependencyContainer.getAvailablePlayersUseCase(entryId).fold(
                                                onSuccess = { availablePlayers = it },
                                                onFailure = { }
                                            )
                                            selectedPlayerId = null
                                            isSubmitting = false
                                        },
                                        onFailure = { e ->
                                            error = e.message
                                            isSubmitting = false
                                        }
                                    )
                                }
                            }
                        )
                    }
                }
            }
            }
        }
    }
}

enum class SortOption(val label: String) {
    OWNERSHIP("Ownership %"),
    SEED("Seed"),
    RANKING("Ranking"),
    MATCH_TIME("Match Time")
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun EmptyState(message: String) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.TextSecondary)
                fontSize(16.px)
            }
        }) {
            Text(message)
        }
    }
}

@Composable
private fun SuccessMessage(message: String, onDismiss: () -> Unit) {
    Div({
        style {
            backgroundColor(rgb(232, 245, 233))
            border(1.px, LineStyle.Solid, Colors.Success)
            borderRadius(8.px)
            padding(16.px)
            marginBottom(16.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                color(Colors.Success)
            }
        }) {
            Span { Text("✓") }
            Text(message)
        }
        Span({
            style {
                cursor("pointer")
                color(Colors.TextSecondary)
            }
            onClick { onDismiss() }
        }) {
            Text("×")
        }
    }
}

@Composable
private fun CurrentPickCard(pick: SurvivorPickEntity, contest: SurvivorContestEntity) {
    Div({
        style {
            backgroundColor(rgb(255, 243, 224))
            border(1.px, LineStyle.Solid, Colors.Warning)
            borderRadius(12.px)
            padding(16.px)
            marginBottom(16.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div {
                Div({
                    style {
                        fontWeight(600)
                        fontSize(14.px)
                        color(Colors.TextPrimary)
                        marginBottom(4.px)
                    }
                }) {
                    Text("Current Pick: ${pick.playerName}")
                }
                Div({
                    style {
                        fontSize(13.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("vs ${pick.opponentName ?: "TBD"}")
                    pick.matchTime?.let { time ->
                        Text(" • $time")
                    }
                }
            }
            Div({
                style {
                    fontSize(12.px)
                    color(Colors.Warning)
                    fontWeight(500)
                }
            }) {
                Text("You can change your pick until match starts")
            }
        }
    }
}

@Composable
private fun EntryStatusCard(entry: SurvivorEntryEntity, contest: SurvivorContestEntity) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(16.px)
            marginBottom(16.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.05)")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(24.px)
            }
        }) {
            StatItem("Status", entry.status.displayName, if (entry.isAlive) Colors.Success else Colors.Error)
            StatItem("Survived", "${entry.survivedRounds} rounds", null)
            if (contest.variation == SurvivorVariation.STRIKE_SYSTEM) {
                StatItem("Strikes", "${entry.strikesUsed}/${contest.strikeLimit}", null)
            }
        }

        contest.roundDeadline?.let { deadline ->
            Div({
                style {
                    textAlign("right")
                }
            }) {
                Div({
                    style {
                        fontSize(11.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("Pick deadline")
                }
                Div({
                    style {
                        fontSize(14.px)
                        fontWeight(500)
                        color(Colors.Error)
                    }
                }) {
                    Text(deadline)
                }
            }
        }
    }
}

@Composable
private fun StatItem(label: String, value: String, valueColor: CSSColorValue?) {
    Div {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(14.px)
                fontWeight(600)
                color(valueColor ?: Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun FilterSortBar(
    filterText: String,
    onFilterChange: (String) -> Unit,
    sortBy: SortOption,
    onSortChange: (SortOption) -> Unit,
    showUsedPlayers: Boolean,
    onShowUsedChange: (Boolean) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            marginBottom(16.px)
            gap(16.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        // Search input
        Input(type = InputType.Text) {
            style {
                padding(10.px, 16.px)
                borderRadius(8.px)
                border(1.px, LineStyle.Solid, Colors.Border)
                fontSize(14.px)
                width(250.px)
                property("outline", "none")
            }
            attr("placeholder", "Search players...")
            value(filterText)
            onInput { event ->
                val target = event.target as? org.w3c.dom.HTMLInputElement
                onFilterChange(target?.value ?: "")
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(16.px)
                alignItems(AlignItems.Center)
            }
        }) {
            // Sort dropdown
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(13.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("Sort by:")
                }
                Select({
                    style {
                        padding(8.px, 12.px)
                        borderRadius(8.px)
                        border(1.px, LineStyle.Solid, Colors.Border)
                        fontSize(14.px)
                        backgroundColor(Colors.White)
                    }
                    onChange { event ->
                        val value = (event.target as? org.w3c.dom.HTMLSelectElement)?.value
                        value?.let { v ->
                            SortOption.entries.find { it.name == v }?.let { onSortChange(it) }
                        }
                    }
                }) {
                    SortOption.entries.forEach { option ->
                        Option(value = option.name, attrs = {
                            if (option == sortBy) attr("selected", "")
                        }) {
                            Text(option.label)
                        }
                    }
                }
            }

            // Show used toggle
            Label(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                    cursor("pointer")
                }
            }) {
                Input(type = InputType.Checkbox) {
                    checked(showUsedPlayers)
                    onChange { onShowUsedChange(!showUsedPlayers) }
                }
                Text("Show used players")
            }
        }
    }
}

@Composable
private fun PlayersGrid(
    players: List<AvailablePlayerEntity>,
    selectedPlayerId: String?,
    onSelectPlayer: (AvailablePlayerEntity) -> Unit,
    currentPickId: String?
) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
            gap(16.px)
            marginBottom(80.px) // Space for submit bar
        }
    }) {
        players.forEach { player ->
            PlayerCard(
                player = player,
                isSelected = selectedPlayerId == player.playerId,
                isCurrentPick = currentPickId == player.playerId,
                onClick = { onSelectPlayer(player) }
            )
        }
    }
}

@Composable
private fun PlayerCard(
    player: AvailablePlayerEntity,
    isSelected: Boolean,
    isCurrentPick: Boolean,
    onClick: () -> Unit
) {
    val isAvailable = player.isAvailable
    val bgColor = when {
        isSelected -> rgb(227, 242, 253)
        isCurrentPick -> rgb(255, 243, 224)
        player.alreadyUsed -> rgb(245, 245, 245)
        player.isMatchLocked -> rgb(255, 235, 238)
        else -> Colors.Surface
    }
    val borderColor = when {
        isSelected -> Colors.Primary
        isCurrentPick -> Colors.Warning
        else -> Colors.Border
    }

    Div({
        style {
            backgroundColor(bgColor)
            borderRadius(12.px)
            border(2.px, LineStyle.Solid, borderColor)
            padding(16.px)
            cursor(if (isAvailable) "pointer" else "not-allowed")
            property("transition", "all 0.2s")
            if (!isAvailable) {
                opacity(0.7)
            }
        }
        onClick { onClick() }
    }) {
        // Header with seed and ownership
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            // Player seed
            player.displaySeed?.let { seed ->
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(12.px)
                        fontWeight(600)
                        padding(2.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text(seed)
                }
            } ?: Div {}

            // Ownership percentage
            OwnershipBadge(player.ownershipPercentage)
        }

        // Player name
        Div({
            style {
                fontWeight(600)
                fontSize(16.px)
                color(Colors.TextPrimary)
                marginBottom(4.px)
            }
        }) {
            Text(player.playerName)
        }

        // Country and ranking
        Div({
            style {
                fontSize(13.px)
                color(Colors.TextSecondary)
                marginBottom(12.px)
            }
        }) {
            player.country?.let { Text("$it • ") }
            player.atpRanking?.let { Text("Ranked #$it") }
        }

        // Matchup
        Div({
            style {
                backgroundColor(Colors.SurfaceVariant)
                borderRadius(8.px)
                padding(12.px)
                marginBottom(12.px)
            }
        }) {
            Div({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                    marginBottom(4.px)
                }
            }) {
                Text("vs")
            }
            Div({
                style {
                    fontWeight(500)
                    fontSize(14.px)
                    color(Colors.TextPrimary)
                }
            }) {
                player.opponentDisplaySeed?.let { seed ->
                    Text("$seed ")
                }
                Text(player.opponentName ?: "TBD")
            }

            // Match time and court
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    marginTop(8.px)
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                }
            }) {
                player.matchTime?.let { Text(it) }
                player.court?.let { Text(it) }
            }
        }

        // Status indicator
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            // Status badge
            val (statusText, statusColor) = when {
                isCurrentPick -> "Current Pick" to Colors.Warning
                player.alreadyUsed -> "Already Used" to Colors.Gray600
                player.isMatchLocked -> "Match Started" to Colors.Error
                else -> "Available" to Colors.Success
            }

            Span({
                style {
                    fontSize(12.px)
                    fontWeight(500)
                    color(statusColor)
                }
            }) {
                Text(statusText)
            }

            // Select indicator
            if (isAvailable && !isCurrentPick) {
                Div({
                    style {
                        width(24.px)
                        height(24.px)
                        borderRadius(50.percent)
                        border(2.px, LineStyle.Solid, if (isSelected) Colors.Primary else Colors.Gray300)
                        backgroundColor(if (isSelected) Colors.Primary else Colors.White)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    if (isSelected) {
                        Span({
                            style {
                                color(Colors.White)
                                fontSize(14.px)
                            }
                        }) {
                            Text("✓")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun OwnershipBadge(percentage: Double) {
    val bgColor = when {
        percentage >= 50 -> rgb(255, 235, 238) // High ownership - red tint
        percentage >= 25 -> rgb(255, 243, 224) // Medium - orange tint
        else -> rgb(232, 245, 233) // Low - green tint (contrarian)
    }
    val textColor = when {
        percentage >= 50 -> rgb(211, 47, 47)
        percentage >= 25 -> rgb(245, 124, 0)
        else -> rgb(56, 142, 60)
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(11.px)
            fontWeight(600)
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Text("${percentage.toInt()}% owned")
    }
}

@Composable
private fun SubmitPickBar(
    selectedPlayer: AvailablePlayerEntity?,
    isSubmitting: Boolean,
    isUpdate: Boolean,
    onSubmit: () -> Unit
) {
    Div({
        style {
            position(Position.Fixed)
            bottom(0.px)
            left(0.px)
            right(0.px)
            backgroundColor(Colors.Surface)
            property("box-shadow", "0 -2px 16px rgba(0, 0, 0, 0.1)")
            padding(16.px, 24.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(16.px)
        }
    }) {
        selectedPlayer?.let { player ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(14.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text(if (isUpdate) "Change pick to:" else "Your pick:")
                }
                Span({
                    style {
                        fontWeight(600)
                        fontSize(16.px)
                        color(Colors.TextPrimary)
                    }
                }) {
                    player.displaySeed?.let { Text("$it ") }
                    Text(player.playerName)
                }
            }
        }

        Button(
            text = when {
                isSubmitting -> "Confirming..."
                isUpdate -> "Update Pick"
                else -> "Confirm Pick"
            },
            onClick = onSubmit,
            variant = "primary"
        )
    }
}
